import { useState } from "react";

import styled from "styled-components/macro";

import {
  Label,
  RequiredIcon,
} from "CrossFeatureComponents/ExtendedFields/styles";
import { DateTime } from "luxon";

import { wrapLongWords } from "UI/styles";

import { formatNumberWithCommas } from "Utilities/Number";

import ExtendedField from "./ExtendedField";

// field, onChange, value, error
export default function EditableExtendedField({
  field,
  value,
  error,
  ...props
}) {
  const [errorText, setErrorText] = useState(false);

  const [mode, setMode] = useState(
    !value ||
      (field.type === "picker" && !value.length) ||
      (field.type === "date" && (!value.month || !value.day)) ||
      (field.type === "address" &&
        (!value.addr1 || !value.city || !value.state || !value.zip))
      ? "edit"
      : "initial"
  );

  const shouldShowEditableField = () => {
    if (mode !== "initial" || !value) return false;
    if (field.type === "picker" && !value.length) return false;
    if (field.type === "date" && (!value.month || !value.day)) return false;
    return true;
  };

  const onEntryValidation = (errorText) => {
    setErrorText(errorText);
  };

  return (
    <>
      {shouldShowEditableField() ? (
        <FieldSignupStyle field={field} value={value} setMode={setMode} />
      ) : (
        <SignupEFWrapper>
          <Icon className={`fa-${field.icon}`} />
          <FieldWrapper>
            <ExtendedField
              field={{ ...field, icon: false }}
              value={value}
              onEntryValidation={onEntryValidation}
              error={errorText || error}
              {...props}
            />
          </FieldWrapper>
        </SignupEFWrapper>
      )}
    </>
  );
}

const Icon = styled.div`
  width: 32px;
  font-size: 24px;
  text-align: center;
  margin-right: 12px;
  flex-grow: 0 !important;
  flex-shrink: 0;
  color: var(--accentColor);

  @media (max-width: 430px) {
    width: 20px;
    font-size: 16px;
    margin-right: 6px;
  }
`;

const SignupEFWrapper = styled.div`
  padding: 8px 0;
  display: flex;
  align-items: center;

  & > div {
    flex-grow: 1;
  }

  & ${Label} {
    font-size: 13px;
    text-transform: uppercase;
  }

  & ${Icon} {
    margin-top: 21px;
  }
`;

const FieldWrapper = styled.div`
  // max-width is so text inputs don't grow too large for very very long words
  // (the calc is to leave room for the icon)
  max-width: calc(100% - 40px);

  @media (max-width: 430px) {
    max-width: calc(100% - 24px);
  }
`;

function FieldSignupStyle({ field, value, setMode }) {
  const getTextValue = () => {
    switch (field.type) {
      case "file":
        if (typeof value === "string") return getTextElement(value);
        else return "";
      case "text":
        return getTextElement(value);
      case "number":
        return field?.rangeContext === "year"
          ? value
          : formatNumberWithCommas(value);
      case "date":
        return getDateStringFromValue(value);
      case "address":
        return getAddressFromValue(value);
      case "picker":
        return getPickerStringFromValues(value);
      default:
        return "";
    }
  };

  function getPickerStringFromValues(selection) {
    if (!selection) return null;

    const options = field.options;

    if (typeof selection === "number") {
      // get value of a single selection
      return options.find(({ ID }) => ID === selection)?.value;
    }

    // create string of all the values combined
    let combinedValues = options
      .filter(({ ID }) => selection.includes(ID))
      .map((option) => option.title)
      .join(", ");

    return combinedValues;
  }

  const getDateStringFromValue = (value) => {
    // DateTime.fromObject() errors if year is an empty string.
    // This was happening when immediately modifying a signup with a yearless date field.
    if (value.year === "") value.year = null;

    const dateObj = DateTime.fromObject(value);

    let formatString = "MMMM d";
    if (value.year) formatString += ", yyyy";

    return dateObj.toFormat(formatString);
  };

  const getTextElement = (value) => (
    <div dangerouslySetInnerHTML={{ __html: value }} />
  );

  const getAddressFromValue = () => {
    return (
      <>
        <div>{value.addr1}</div>
        <AddressSubLine>
          {value.addr2 && <div>{value.addr2}</div>}
          {value.city}, {value.state} {value.zip}
        </AddressSubLine>
      </>
    );
  };

  return (
    <PreviewWrapper>
      <Icon className={`fa-${field.icon}`} />
      <PreviewContent>
        <PreviewLabel>
          {field.title}
          {field.inputIsRequired && <RequiredIcon />}
        </PreviewLabel>
        <Text>{getTextValue()}</Text>
      </PreviewContent>
      <EditButtonWrapper>
        <EditButton
          className="fad fa-pencil"
          onClick={() => setMode("edit")}
          data-cy="EditableExtendedField_EditButton"
        />
      </EditButtonWrapper>
    </PreviewWrapper>
  );
}

const PreviewWrapper = styled.div`
  display: flex;
  padding: 8px 0;
  align-items: center;
`;

const PreviewContent = styled.div``;

const PreviewLabel = styled.div`
  font-size: 13px;
  text-transform: uppercase;
  font-weight: 400;
  color: #4c5464;
`;

const Text = styled.div`
  font-size: 18px;
  color: #222;
  margin-right: 6px;
  font-weight: 400;
  ${wrapLongWords}

  & p {
    margin: 0;
  }
`;

const EditButtonWrapper = styled.div`
  flex-shrink: 0;
  margin-left: 10px;
  display: flex;
  align-items: center;
`;
const EditButton = styled.div`
  background: #98a4a5;
  color: #eceff0;
  font-size: 16px;
  height: 30px;
  line-height: 30px;
  width: 30px;
  text-align: center;
  border-radius: 40px;
  cursor: pointer;
  transition: transform 0.2s, opacity 0.2s, visibility 0.2s, background 0.2s;

  @media (pointer: fine) {
    opacity: 0;
    transform: scale(0.8);
    visibility: hidden;

    ${PreviewWrapper}:hover & {
      opacity: 1;
      transform: none;
      visibility: visible;
    }
  }

  &:hover:not(:active) {
    transform: scale(1.1);
    background: var(--accentColor);
  }
`;
const AddressSubLine = styled.div`
  font-size: 14px;
`;
